import { Injectable, OnDestroy } from '@angular/core';
import { StudyService } from '@services/study.service';
import { LoggingService } from '@services/logging.service';
import { BooleanSearchService } from '@services/boolean-search.service';
import { StudySearchStrategyProcess } from '../mystudies/_study_constants';
import { KeywordsExtracted } from '@models/keywordsExtracted.model';
import { KeywordsSelected } from '@models/keywordsSelected.model';
import { StudySearchStrategy } from '@models/searchStrategy.model';
import { FunctionCategory } from '@models/functionCategory.model';
import { firstValueFrom, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { KeywordExtractionSelection } from '@models/keywordExtractionSelection.model';
import { SearchStrategyService } from './search-strategy.service';
import { UserNotificationService } from '@services/user-notification.service';
import { BDMatrixFilter } from '@models/bdMatrixFilter.model';
import { KeywordsExtractedList } from '@models/keywordsExtractedList.model';
import { FunctionKeyword } from '@models/functionKeyword.model';
import { SignalRService } from './signalr.service';
import { KeywordType } from '../mystudies/_study_constants';

@Injectable({
  providedIn: 'root',
})
export class KeywordExtractionService implements OnDestroy {
  public studySearchStrategy: StudySearchStrategy;
  public extractedKeywords: KeywordsExtracted[] = [];
  public selectedKeywords: KeywordsSelected[] = [];
  public extractedKeywordsCount: number;
  public extractedKeywordsFilteredCount: number;

  public functionCategories: FunctionCategory[] = [];
  public keywordExtractionStatus: StudySearchStrategyProcess;

  public loadingKeywords = false;

  // Observable sources
  private keywordSelectedSource = new Subject<KeywordsSelected>();
  private keywordRemovedSource = new Subject<KeywordsSelected>();
  private keywordFailedSource = new Subject<KeywordsSelected>();

  // Observable streams
  keywordSelected$ = this.keywordSelectedSource.asObservable();
  keywordRemoved$ = this.keywordRemovedSource.asObservable();
  keywordFailed$ = this.keywordFailedSource.asObservable();

  keywordBooleanChangedSource = new Subject<KeywordsSelected>();
  keywordBooleanChanged$ = this.keywordBooleanChangedSource.asObservable();

  // Boolean validators
  includeFunctionBooleanValid = true;
  includeProductBooleanValid = true;
  excludeBooleanValid = true;

  // Boolean logic strings before any unsaved changes
  includeFunctionBooleanLogic: string = null;
  includeProductBooleanLogic: string = null;
  excludeBooleanLogic: string = null;

  havingUnsavedChanges = false;

  private signalrHubExtractionCompleted = 'keywordsExtractionStatusUpdate';

  constructor(
    private http: HttpClient,
    private router: Router,
    private logger: LoggingService,
    private studyService: StudyService,
    searchStrategyService: SearchStrategyService,
    private booleanSearchService: BooleanSearchService,
    private userNotificationService: UserNotificationService,
    private signalRService: SignalRService
  ) {
    this.studySearchStrategy = searchStrategyService.studySearchStrategy;

    this.signalRService.hubConnection.on(this.signalrHubExtractionCompleted, (strategyId: string, status: number) => {
      if (this.studySearchStrategy.studySearchStrategyId === +strategyId) {
        if (StudySearchStrategyProcess.Completed === status) {
          this.keywordExtractionStatus = StudySearchStrategyProcess.Completed;
        } else if (StudySearchStrategyProcess.Failed === status) {
          this.keywordFailedSource.next(null);
        }
      }
    });
  }

  ngOnDestroy() {
    this.signalRService.hubConnection.off(this.signalrHubExtractionCompleted);
  }

  public async getKeywords(): Promise<void> {
    this.logger.info(this.constructor.name, 'getKeywords', 'Start');
    this.loadingKeywords = true;
    try {
      if (this.studySearchStrategy) {
        this.keywordExtractionStatus = await this.studyService.getKeywordExtractionStatus(this.studySearchStrategy.studySearchStrategyId);

        await Promise.all([
          this.GetKeywordsExtracted(this.studySearchStrategy.studySearchStrategyId),
          this.GetKeywordsSelected(this.studySearchStrategy.studySearchStrategyId),
        ]).then((values) => {
          this.extractedKeywords = values[0];
          this.selectedKeywords = values[1];
          this.initializeSelectedKeywords();
          this.initializeFunctionCategories();
          this.keywordSelectedSource.next(null);
        });

        this.functionCategories = await this.studyService.GetAllActiveFunctionKeywords();
      }
    } finally {
      this.loadingKeywords = false;
      this.logger.info(this.constructor.name, 'getKeywords', 'End');
    }
  }

  public async initializeKeywordScreening() {
    const selectedKeywordPromise = this.GetKeywordsSelected(this.studySearchStrategy.studySearchStrategyId);
    const functionCategoriesPromise = this.studyService.GetAllActiveFunctionKeywords();
    const keywordExtractionStatusPromise = this.studyService.getKeywordExtractionStatus(this.studySearchStrategy.studySearchStrategyId);

    this.selectedKeywords = await selectedKeywordPromise;
    this.functionCategories = await functionCategoriesPromise;
    this.initializeFunctionCategories();
    this.keywordExtractionStatus = await keywordExtractionStatusPromise;
    this.keywordSelectedSource.next(null);
  }

  public async getFilteredExtractedKeywords(filter: BDMatrixFilter) {
    filter.studySearchStrategyId = this.studySearchStrategy.studySearchStrategyId;
    const extractedKeywordsList = await this.GetFilteredKeywordsExtracted(filter);
    this.extractedKeywords = extractedKeywordsList.extractedKeywords;
    this.extractedKeywordsFilteredCount = extractedKeywordsList.extractedKeywordsCount;
    this.initializeSelectedKeywords();
  }

  public clear(): void {
    this.extractedKeywords = [];
    this.selectedKeywords = [];
    this.keywordExtractionStatus = StudySearchStrategyProcess.NotStarted;
  }

  public selectKeyword(extractedKeyword: KeywordsExtracted, keywordTypeId: KeywordType): void {
    const selectedKeyword: KeywordsSelected = this.selectedKeywords.find((k) => k.keywordsExtractedId === extractedKeyword.keywordsExtractedId);
    extractedKeyword.keywordTypeId = keywordTypeId;
    if (selectedKeyword) {
      // Change the selection of a currently selected keyword
      this.keywordRemovedSource.next(selectedKeyword);
      selectedKeyword.keywordTypeId = keywordTypeId;
      selectedKeyword.isActive = true;
      this.keywordSelectedSource.next(selectedKeyword);
    } else {
      // Newly selected keyword
      const keyword: KeywordsSelected = {
        studySearchStrategyId: this.studySearchStrategy.studySearchStrategyId,
        keywordsExtractedId: extractedKeyword.keywordsExtractedId,
        functionKeywordId: null,
        keyword: extractedKeyword.keyword,
        keywordTypeId: keywordTypeId,
        isEnabled: true,
        isActive: true,
        extractedKeyword: extractedKeyword,
      };
      this.selectedKeywords.push(keyword);
      this.keywordSelectedSource.next(keyword);
    }
  }

  public removeKeyword(selectedKeyword: KeywordsSelected): void {
    if (selectedKeyword) {
      selectedKeyword.isActive = false;
      this.keywordRemovedSource.next(selectedKeyword);

      const extractedKeyword: KeywordsExtracted = this.extractedKeywords.find((k) => k.keywordsExtractedId === selectedKeyword.keywordsExtractedId);
      if (extractedKeyword) {
        extractedKeyword.keywordTypeId = null;
      }
    }
  }

  public addPredefinedList(list: FunctionCategory, keywordTypeId: KeywordType) {
    list.functionKeywords.forEach((k) => this.addPredefinedKeyword(k, keywordTypeId));
  }

  public addPredefinedKeyword(functionKeyword: FunctionKeyword, keywordTypeId: KeywordType) {
    const keyword: KeywordsSelected = {
      studySearchStrategyId: this.studySearchStrategy.studySearchStrategyId,
      keywordsExtractedId: null,
      functionKeywordId: functionKeyword.functionKeywordId,
      keyword: functionKeyword.keyword,
      keywordTypeId: keywordTypeId,
      isEnabled: true,
      isActive: true,
      extractedKeyword: null,
    };
    this.selectedKeywords.push(keyword);
    this.keywordSelectedSource.next(keyword);
  }

  public getSelectedKeywordFromFunction(keyword: FunctionKeyword) {
    const selectedKeyword: KeywordsSelected = {
      studySearchStrategyId: this.studySearchStrategy.studySearchStrategyId,
      keywordsExtractedId: null,
      functionKeywordId: keyword.functionKeywordId,
      keyword: keyword.keyword,
      keywordTypeId: null,
      isEnabled: true,
      isActive: true,
      extractedKeyword: null,
    };

    return selectedKeyword;
  }

  public removePredefinedList(list: FunctionCategory) {
    list.keywordTypeId = null;
    const categoryKeywords = this.getCategoryKeywords(list);
    categoryKeywords.forEach((keyword) => (keyword.isActive = false));
  }

  public async addCustomKeyword(newKeyword: string) {
    const keyword: KeywordsSelected = this.selectedKeywords.find((k) => k.keyword === newKeyword);
    if (!keyword) {
      const keywordAsExtracted = new KeywordsExtracted();
      keywordAsExtracted.studySearchStrategyId = this.studySearchStrategy.studySearchStrategyId;
      keywordAsExtracted.keyword = newKeyword;
      const result = await this.studyService.AddCustomKeyword(keywordAsExtracted);
      this.extractedKeywords.push(result);
    }
  }

  public getTotalCount(): number {
    return this.selectedKeywords ? this.selectedKeywords.filter((k) => k.isActive).length : 0;
  }

  public getWordsIncludedCount(): number {
    return this.selectedKeywords
      ? this.selectedKeywords.filter(
          (k) => (k.keywordTypeId === KeywordType.IncludeFunction || k.keywordTypeId === KeywordType.IncludeProduct) && k.isActive
        ).length
      : null;
  }

  public getWordsExcludedCount(): number {
    return this.selectedKeywords ? this.selectedKeywords.filter((k) => k.keywordTypeId === KeywordType.Exclude && k.isActive).length : null;
  }

  public getKeywordSuggestions(): KeywordsExtracted[] {
    return this.extractedKeywords.filter((k) => k.keywordTypeId === null);
  }

  public getCategoryKeywords(category: FunctionCategory): KeywordsSelected[] {
    const functionKeywordIds = category.functionKeywords.map((k) => k.functionKeywordId);
    return this.selectedKeywords.filter((k) => functionKeywordIds.indexOf(k.functionKeywordId) > -1);
  }

  public validateBoolean(): boolean {
    this.logger.info(this.constructor.name, 'validateBoolean', 'Start');
    this.excludeBooleanValid = true;
    if (this.studySearchStrategy) {
      if (this.studySearchStrategy.includeFunctionBooleanLogic) {
        try {
          // this.booleanSearch = this.booleanSearch.toUpperCase();
          const rpn = this.booleanSearchService.parseBooleanString(this.studySearchStrategy.includeFunctionBooleanLogic.toUpperCase());
          // const jsonLogic = this.booleanSearchService.booleanToJsonLogic(rpn);
          if (rpn.length < 1) {
            this.includeFunctionBooleanValid = false;
          }
          if (!this.validateEntries(rpn, KeywordType.IncludeFunction)) {
            this.includeFunctionBooleanValid = false;
          }
        } catch (ex) {
          this.includeFunctionBooleanValid = false;
          this.logger.error(this.constructor.name, 'validateBoolean_includeFunction', ex);
        }
      }

      if (this.studySearchStrategy.includeProductBooleanLogic) {
        try {
          // this.booleanSearch = this.booleanSearch.toUpperCase();
          const rpn = this.booleanSearchService.parseBooleanString(this.studySearchStrategy.includeProductBooleanLogic.toUpperCase());
          // const jsonLogic = this.booleanSearchService.booleanToJsonLogic(rpn);
          if (rpn.length < 1) {
            this.includeProductBooleanValid = false;
          }
          if (!this.validateEntries(rpn, KeywordType.IncludeProduct)) {
            this.includeProductBooleanValid = false;
          }
        } catch (ex) {
          this.includeProductBooleanValid = false;
          this.logger.error(this.constructor.name, 'validateBoolean_includeProduct', ex);
        }
      }

      if (this.studySearchStrategy.excludeBooleanLogic) {
        try {
          // this.booleanSearch = this.booleanSearch.toUpperCase();
          const rpn = this.booleanSearchService.parseBooleanString(this.studySearchStrategy.excludeBooleanLogic.toUpperCase());
          // const jsonLogic = this.booleanSearchService.booleanToJsonLogic(rpn);
          if (rpn.length < 1) {
            this.excludeBooleanValid = false;
          }
          if (!this.validateEntries(rpn, KeywordType.Exclude)) {
            this.excludeBooleanValid = false;
          }
        } catch (ex) {
          this.excludeBooleanValid = false;
          this.logger.error(this.constructor.name, 'validateBoolean_exclude', ex);
        }
      }
    }

    this.logger.info(
      this.constructor.name,
      'validateBoolean',
      `End: excludeBooleanValid:${this.excludeBooleanValid} includeFunctionBooleanValid:${this.includeFunctionBooleanValid} includeProductBooleanValid:${this.includeProductBooleanValid}`
    );
    return this.excludeBooleanValid && this.includeFunctionBooleanValid && this.includeProductBooleanValid;
  }

  public saveSelectedKeywords(): Promise<void> {
    return this.studyService.SaveSelectedKeywords(this.selectedKeywords);
  }

  public async ExtractKeywords(selection: KeywordExtractionSelection): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ExtractKeywords', 'ExtractKeywords', 'Start');
    const msgSubject = `Extract Keywords for Study ${this.studySearchStrategy.studyId ? this.studySearchStrategy.studyId : ''}`;

    const flag: boolean = <boolean>await firstValueFrom(this.http.post<boolean>(`/api/study/ExtractKeywords`, selection)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'ExtractKeywords', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      } else {
        this.userNotificationService.addNotification(`${msgSubject}: ${e.message}`, true);
      }
    });
    return flag;
  }

  public async GetKeywordsExtracted(studySearchStrategyId: number) {
    this.logger.info(this.constructor.name, 'GetKeywordsExtracted', 'Start');
    let extractedKeywords: KeywordsExtracted[];
    try {
      extractedKeywords = <KeywordsExtracted[]>await firstValueFrom(
        this.http.get<KeywordsExtracted[]>(`/api/study/studySearchStrategy/${studySearchStrategyId}/keywordsextracted`)
      ).catch((e) => {
        this.logger.httpError(this.constructor.name, 'GetKeywordsExtracted', e);
      });
      return extractedKeywords;
    } finally {
      this.logger.info(this.constructor.name, 'GetKeywordsExtracted', `End: ${extractedKeywords.length} items`);
    }
  }

  public async GetFilteredKeywordsExtracted(filter: BDMatrixFilter): Promise<KeywordsExtractedList> {
    this.logger.info(this.constructor.name, 'GetFilteredKeywordsExtracted', 'Start');
    let extractedKeywords: KeywordsExtractedList;
    try {
      extractedKeywords = <KeywordsExtractedList>await firstValueFrom(
        this.http.post<KeywordsExtractedList>('/api/study/studySearchStrategy/keywordsextracted', filter)
      ).catch((e) => {
        this.logger.httpError(this.constructor.name, 'GetFilteredKeywordsExtracted', e);
      });
      return extractedKeywords;
    } finally {
      this.logger.info(this.constructor.name, 'GetFilteredKeywordsExtracted', `End: ${extractedKeywords.extractedKeywordsCount} items`);
    }
  }

  public async GetKeywordsSelected(studySearchStrategyId: number) {
    this.logger.info(this.constructor.name, 'GetKeywordsSelected', 'GetKeywordsSelected', 'Start');
    let selectedKeywords: KeywordsSelected[] = [];

    try {
      if (studySearchStrategyId && studySearchStrategyId > 0) {
        selectedKeywords = <KeywordsSelected[]>await firstValueFrom(
          this.http.get<KeywordsSelected[]>(`/api/study/studySearchStrategy/${studySearchStrategyId}/keywordsselected`)
        ).catch((e) => {
          this.logger.httpError(this.constructor.name, 'GetKeywordsSelected', e);
        });
      }
      return selectedKeywords;
    } finally {
      this.logger.info(this.constructor.name, 'GetKeywordsSelected', `End: ${selectedKeywords.length} items`);
    }
  }

  public async GetExtractedKeywordsCount(studySearchStrategyId: number) {
    this.logger.info(this.constructor.name, 'GetExtractedKeywordsCount', 'GetExtractedKeywordsCount', 'Start');
    const extractedKeywordCount = <number>await firstValueFrom(
      this.http.get<number>(`/api/study/studySearchStrategy/${studySearchStrategyId}/keywordsextractedcount`)
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetExtractedKeywordsCount', e);
    });
    return extractedKeywordCount;
  }

  private initializeSelectedKeywords(): void {
    this.selectedKeywords.forEach((k) => {
      if (k.keywordsExtractedId) {
        const extractedKeyword = this.extractedKeywords.find((ek) => ek.keywordsExtractedId === k.keywordsExtractedId);
        if (extractedKeyword) {
          if (!k.isActive) {
            extractedKeyword.keywordTypeId = null;
          } else {
            extractedKeyword.keywordTypeId = k.keywordTypeId;
          }
          k.extractedKeyword = extractedKeyword;
        }
      }
    });
  }

  private initializeFunctionCategories(): void {
    this.functionCategories.forEach((category) => {
      const keywordIds: number[] = category.functionKeywords.map((f) => f.functionKeywordId);
      const item: KeywordsSelected = this.selectedKeywords.find((k) => keywordIds.indexOf(k.functionKeywordId) > -1);
      if (item) {
        category.keywordTypeId = item ? item.keywordTypeId : null;
      }
    });
  }

  private validateEntries(rpn: string[], keywordType: KeywordType): boolean {
    // Ensure that the boolean search string contains valid filter selections
    const options: string[] = ['AND', 'OR', 'NOT'];
    this.selectedKeywords.forEach((element) => {
      if (element.keywordTypeId === keywordType && element.isEnabled) {
        options.push(element.keyword.toUpperCase());
      }
    });
    return rpn.every((element) => options.includes(element));
  }

  // resets keyword extraction status re-enables keyword extract btn and also hides keyword selection from keyword screening
  public enableKeywordExtraction(StudySearchStrategyId: number) {
    return this.http.patch(`/api/study/StudySearchStrategy/${StudySearchStrategyId}/EnableKeywordExtraction`, {});
  }

  public async getSelectedKeywords(): Promise<KeywordsSelected[]> {
    this.logger.info(this.constructor.name, 'getKeywords', 'Start');

    if (this.studySearchStrategy) {
      return await this.GetKeywordsSelected(this.studySearchStrategy.studySearchStrategyId);
    }

    this.logger.info(this.constructor.name, 'getSelectedKeywords', 'End');
    return [];
  }
}
